/* eslint-disable camelcase */
import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { createSelector, createStructuredSelector } from 'reselect';
import store from 'store/Store';
import watch from 'redux-watch';

// Selectors
import { superChatSelector } from 'ai/selectors/superChatSelector';
import { coreUserDataSelector } from 'viewModel/selectors/user/coreUserDataSelector';
import modalsSelector from 'selectors/modals/modalsSelector';
import { pageSelector } from 'selectors/page/pageSelector';
import { showWelcomeMat } from 'ai/selectors/testTarget/showWelcomeMatSelector';

// Services
import sdnApi from 'ai/services/sdn';

// Actions
import SuperChatActions from 'ai/actions/superChatActions';

// Utils
import FrameworkUtils from 'utils/framework';
import LanguageLocaleUtils from 'utils/LanguageLocale';
import { getSuperChatConfig, setSuperChatConfig } from 'ai/utils/sessionStorage';
import { getAnonymousId } from 'ai/utils/aiBeautyChat';

// Constants
import { INTENTIONS, ENTRYPOINTS } from 'ai/constants/superchat';
import { CATEGORIES } from 'ai/constants/categories';

// Locales
import EN from 'ai/components/SuperChat/locales/SuperChat_en_US.js';
import FR from 'ai/components/SuperChat/locales/SuperChat_fr_CA.js';

const { wrapHOC, wrapHOCComponent } = FrameworkUtils;
const { getTextFromResource, getLocalisedDataFromResource, getCurrentLanguage, getCurrentCountry } = LanguageLocaleUtils;
const getText = getLocalisedDataFromResource(EN, FR);
const country = getCurrentCountry();
const language = getCurrentLanguage();

const { managerSession, superChatConfig, previousConversations } = sdnApi;

const localization = createStructuredSelector({
    title: getTextFromResource(getText, 'title'),
    placeholder: getTextFromResource(getText, 'placeholder'),
    errorMessage: getTextFromResource(getText, 'errorMessage'),
    resendYourMessage: getTextFromResource(getText, 'resendYourMessage'),
    lookingForAGift: getTextFromResource(getText, 'lookingForAGift')
});

const functions = (dispatch, _ownProps) => ({
    createSession: async sessionFound => {
        return managerSession(sessionFound).then(data => data);
    },
    closeSuperChat: () => {
        dispatch(SuperChatActions.showSuperChat(false));
    },
    fetchPreviousConversations: (clientId, anonymousId) => {
        return previousConversations(clientId, anonymousId)
            .then(data => data)
            .catch(_ => []);
    },
    storeSuperChatConfig: config => {
        dispatch(SuperChatActions.superChatConfig(config));
    },
    setRequestInFlight: isRequestInFlight => {
        dispatch(SuperChatActions.setRequestInFlight(isRequestInFlight));
    },
    openSuperChatFromGiftFinder: (prompt, defaultPrompt) => {
        const data = {
            entrypoint: ENTRYPOINTS.PLP,
            category_id: CATEGORIES.GIFT,
            selectedPrompt: prompt || defaultPrompt
        };

        dispatch(SuperChatActions.showSuperChat(true, [], data));
    },
    openSuperChatFromDeeplink: (intention, prompt, defaultPrompt) => {
        const selectedPrompt = prompt || defaultPrompt || undefined;
        const data =
            intention === INTENTIONS.GIFT_FINDER
                ? { entrypoint: ENTRYPOINTS.DEFAULT, category_id: CATEGORIES.GIFT, selectedPrompt }
                : { entrypoint: ENTRYPOINTS.DEFAULT, selectedPrompt };

        dispatch(SuperChatActions.showSuperChat(true, [], data));
    },
    setWelcomeMatShown: shown => {
        dispatch(SuperChatActions.setWelcomeMatShown(shown));
    }
});

const fields = createSelector(
    superChatSelector,
    coreUserDataSelector,
    localization,
    modalsSelector,
    pageSelector,
    showWelcomeMat,
    (superChat, user, locales, modals, page, shouldShowWelcomeMat) => {
        const clientId = user?.biId;
        const isSignInModalOpen = modals?.showSignInModal || modals?.showSignInWithMessagingModal || modals?.showAuthenticateModal;

        return {
            localization: locales,
            clientId,
            isSignInModalOpen,
            page: page,
            productBrandName: page?.product?.productDetails?.brand?.displayName,
            productName: page?.product?.productDetails?.displayName,
            nthCategoryName: page?.nthCategory?.displayName,
            shouldShowWelcomeMat,
            ...superChat
        };
    }
);

const withSuperChatProps = compose(
    wrapHOC(connect(fields, functions)),
    wrapHOC(WrappedComponent => {
        class SuperChatProps extends React.Component {
            state = {
                config: null,
                anonymousId: null
            };

            componentDidMount() {
                const watchLocation = watch(store.getState, 'historyLocation');
                const historyLocation = store.getState().historyLocation;
                const { clientId } = this.props;
                const superChatConfigCached = getSuperChatConfig();
                const anonymousId = getAnonymousId(clientId);

                store.subscribe(
                    watchLocation(location => {
                        if (location?.queryParams?.intention?.[0]) {
                            this.openAIBeautyChat(location);
                        }
                    })
                );

                if (historyLocation?.queryParams?.intention?.[0]) {
                    this.openAIBeautyChat(historyLocation);
                }

                if (superChatConfigCached) {
                    this.setState({ config: superChatConfigCached, anonymousId });
                    this.props.storeSuperChatConfig(superChatConfigCached);

                    return;
                }

                superChatConfig(language, country, clientId, anonymousId).then(data => {
                    setSuperChatConfig(data?.data?.config);
                    this.props.storeSuperChatConfig(data?.data?.config);
                    this.setState({ config: data?.data?.config, anonymousId });
                });
            }

            openAIBeautyChat = location => {
                const { openSuperChatFromDeeplink } = this.props;
                const intention = location?.queryParams?.intention?.[0];
                const prompt = location?.queryParams?.prompt?.[0];

                switch (intention) {
                    case INTENTIONS.GIFT_FINDER:
                        openSuperChatFromDeeplink(INTENTIONS.GIFT_FINDER, prompt, this.props.localization?.lookingForAGift);

                        break;
                    case INTENTIONS.DEFAULT:
                        openSuperChatFromDeeplink(INTENTIONS.DEFAULT, prompt);

                        break;
                    default:
                        break;
                }
            };

            render() {
                const { config, anonymousId } = this.state;
                const { showSuperChat } = this.props;

                if (!showSuperChat || !config) {
                    return null;
                }

                return (
                    <WrappedComponent
                        {...this.props}
                        config={config}
                        language={language}
                        country={country}
                        anonymousId={anonymousId}
                    />
                );
            }
        }

        return wrapHOCComponent(SuperChatProps, 'SuperChatProps', [WrappedComponent]);
    })
);

export {
    withSuperChatProps, fields, functions
};
